import { useState } from "react";
import { toast } from "sonner";
import { Copy, DownloadSimple, PaperPlaneTilt, PencilSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { downloadPdf } from "@/lib/download";
import { EMAIL_RE } from "@/components/gift/GiftForm";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
const STATUS = {
  active: ["Pending · not claimed yet", "bg-[#FFF1CC] text-[#8A5A10]"],
  redeemed: ["Claimed", "bg-[#EAF1EE] text-[#4A6B5D]"],
  expired: ["Expired", "bg-[#F4EFE6] text-[#8A7F76]"],
  refunded: ["Refunded", "bg-[#F4EFE6] text-[#8A7F76]"],
  on_hold: ["On hold", "bg-[#FFE1CF] text-[#A84A32]"],
};
const err = (e, f) => {
  const d = e.response?.data?.detail;
  toast.error(typeof d === "string" && !d.includes("<") && !/cloudflare|origin web server/i.test(d) ? d : f);
};

const inviteText = (g) => {
  if (g.status === "redeemed") return `Claimed on ${fmt(g.redeemed_at)}`;
  if (g.invite_status === "scheduled") return `Delivery scheduled for ${fmt(g.deliver_on)}, 8am Sydney time`;
  if (g.invite_status === "failed") return "The invitation email couldn't be sent. Please check the address and resend.";
  if (g.invite_sent_at && g.claim_expires && new Date(g.claim_expires) < new Date()) return "The claim link has expired. Resend the invitation to send a fresh link.";
  if (g.invite_sent_at) return `Delivered by email ${fmt(g.invite_sent_at)} · link works until ${fmt(g.claim_expires)}`;
  return g.recipient_email ? "Invitation not sent yet" : "Share the code or certificate in person";
};

const EmailEditor = ({ g, onDone }) => {
  const [email, setEmail] = useState(g.recipient_email || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put(`/gifts/${g.code}/recipient`, { recipient_email: email.trim() });
      if (r.data.invite_status === "failed") toast.warning("Email updated, but the invitation couldn't be sent. Please check the address and resend.");
      else toast.success(g.invite_status === "scheduled" ? "Email updated. The invitation will go to the new address." : "Email updated and a fresh invitation sent. The old link no longer works.");
      onDone(true);
    } catch (e2) { err(e2, "Couldn't update the email."); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-3 flex flex-col gap-2 sm:flex-row">
      <input data-testid={`gift-email-edit-${g.code}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
        className="min-w-0 flex-1 rounded-full border border-[#EBE4DA] px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#36858B]/40" />
      <div className="flex gap-2">
        <button data-testid={`gift-email-save-${g.code}`} disabled={busy || !EMAIL_RE.test(email.trim())}
          className="rounded-full bg-[#36858B] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        <button type="button" onClick={() => onDone(false)} className="rounded-full px-4 py-2.5 text-sm text-[#6E665E] hover:bg-[#F4EFE6]">Cancel</button>
      </div>
    </form>
  );
};

export const GiftRow = ({ g, highlight, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [label, cls] = STATUS[g.status] || STATUS.active;
  const open = g.status === "active";
  const download = async () => {
    setBusy("pdf");
    try { await downloadPdf(`/gifts/${g.code}/certificate`, `Gift certificate ${g.recipient_name}`); }
    catch { toast.error("We couldn't download the certificate. Please try again."); }
    finally { setBusy(""); }
  };
  const resend = async () => {
    setBusy("resend");
    try { await api.post(`/gifts/${g.code}/resend`); toast.success(`Invitation sent to ${g.recipient_email}. Earlier links no longer work.`); onChanged(); }
    catch (e) { err(e, "Couldn't resend the invitation."); } finally { setBusy(""); }
  };
  const copy = () => navigator.clipboard?.writeText(g.code).then(() => toast.success("Code copied"));
  const btn = "flex min-h-[44px] items-center gap-1.5 rounded-full px-4 text-sm font-semibold";
  return (
    <li data-testid={`gift-row-${g.code}`} className={`rounded-xl bg-white p-4 ring-1 ${highlight ? "ring-2 ring-[#E06D53]" : "ring-[#EBE4DA]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif text-xl text-[#2D2824]">For {g.recipient_name}{g.occasion ? ` · ${g.occasion}` : ""}</p>
          {g.recipient_email && <p data-testid={`gift-email-${g.code}`} className="break-all text-sm text-[#4A4540]">{g.recipient_email}</p>}
          <p className="mt-0.5 text-xs text-[#8A7F76]">Order {g.order_number} · bought {fmt(g.created_at)} · valid until {fmt(g.expires_at)}</p>
        </div>
        <span data-testid={`gift-status-${g.code}`} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>
      </div>
      <p data-testid={`gift-invite-${g.code}`} className="mt-2 text-sm text-[#6E665E]">{inviteText(g)}</p>
      {editing && <EmailEditor g={g} onDone={(changed) => { setEditing(false); if (changed) onChanged(); }} />}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span data-testid={`gift-code-${g.code}`} className="rounded-lg bg-[#FAF6F0] px-3 py-2 font-mono text-base tracking-wider text-[#2D2824]">{g.code}</span>
        <button onClick={copy} data-testid={`gift-copy-${g.code}`} className={`${btn} font-normal text-[#36858B] hover:bg-[#EBF4F5]`}><Copy size={16} /> Copy</button>
        {open && g.recipient_email && (
          <button onClick={resend} disabled={!!busy} data-testid={`gift-resend-${g.code}`} className={`${btn} bg-[#36858B] text-white hover:bg-[#245E63] disabled:opacity-60`}>
            <PaperPlaneTilt size={16} /> {busy === "resend" ? "Sending…" : g.invite_status === "scheduled" ? "Send now" : "Resend invitation"}
          </button>
        )}
        {open && !editing && (
          <button onClick={() => setEditing(true)} data-testid={`gift-change-email-${g.code}`} className={`${btn} border border-[#36858B] text-[#36858B] hover:bg-[#EBF4F5]`}>
            <PencilSimple size={16} /> {g.recipient_email ? "Correct email" : "Add email"}
          </button>
        )}
        <button onClick={download} disabled={!!busy} data-testid={`gift-download-${g.code}`} className={`${btn} bg-[#E06D53] text-white hover:bg-[#C85B43] disabled:opacity-60`}>
          <DownloadSimple size={16} /> {busy === "pdf" ? "Preparing…" : "Printable certificate"}
        </button>
      </div>
    </li>
  );
};

export const GiftList = ({ gifts, highlight, onChanged }) =>
  gifts.length > 0 && (
    <section data-testid="gift-list" className="mt-12">
      <h2 className="font-serif text-2xl text-[#2D2824]">Gifts you've given</h2>
      <p className="mt-1 text-sm text-[#6E665E]">See whether each gift is pending or claimed. Their diary stays private to them.</p>
      <ul className="mt-4 space-y-3">
        {gifts.map((g) => <GiftRow key={g.code} g={g} highlight={highlight === g.code} onChanged={onChanged} />)}
      </ul>
    </section>
  );
